import React from 'react'
import cx from 'classnames'
import Image from 'next/image'
import Link from 'next/link'
import { Button, ConditionalWrapper } from '~elements'
import { ButtonProps, ResponsiveImage, EyebrowBackgroundColorType } from '~types'
import { NEXT_IMAGE_QUALITY } from '~constants'
import styles from './CarouselCard.module.scss'

export type tagColor = 'blue' | 'orange' | 'purple' | 'yellow'

export interface StatusTagProps {
  color?: EyebrowBackgroundColorType
  text?: string
}

export interface CarouselCardProps {
  responsiveImage: ResponsiveImage
  primaryButton?: ButtonProps
  secondaryButton?: ButtonProps
  className?: string
  imageHref?: string
  tabIndex?: number
  hasStatusTag: boolean
  tagText?: string
  tagColor?: EyebrowBackgroundColorType
  isChildOfHeroCarousel?: boolean
}

const StatusTag = ({ color, text }: StatusTagProps) => {
  return <div className={cx(styles.statusTag, `has-bg-color-${color}`)}>{text}</div>
}

const CarouselCard = ({
  responsiveImage,
  primaryButton,
  secondaryButton,
  className,
  imageHref,
  tabIndex,
  hasStatusTag,
  tagText,
  tagColor,
  isChildOfHeroCarousel = false
}: CarouselCardProps) => {
  // Destructure responsive multimedia fields
  const { imageSm, imageMd, imageXl } = responsiveImage ?? {}
  const fallbackImage = imageXl
  const fallbackAlt = imageXl?.altText ?? ''
  // Analytics
  const linkModule = 'carousel'
  const linkLocation = isChildOfHeroCarousel ? 'hero_carousel' : ''

  return (
    <div className={cx(styles.carouselCard, className)}>
      <div className={styles.imageContainer}>
        <ConditionalWrapper
          condition={Boolean(imageHref)}
          wrapper={children => (
            <Link
              draggable={false}
              href={imageHref!}
              tabIndex={tabIndex}
              aria-label={`View details for ${fallbackAlt || 'event'}`}
            >
              {children}
            </Link>
          )}
        >
          <picture>
            {imageSm?.url && <source srcSet={imageSm.url} media='(max-width: 767px)' />}
            {imageMd?.url && <source srcSet={imageMd.url} media='(max-width: 1440px)' />}
            {fallbackImage?.url && (
              <Image
                style={{}}
                src={fallbackImage.url}
                alt={fallbackAlt}
                fill
                quality={NEXT_IMAGE_QUALITY}
                className={styles.backgroundImage}
              />
            )}
          </picture>
        </ConditionalWrapper>
      </div>
      {hasStatusTag && (
        <div className={styles.statusTagContainer}>
          <StatusTag color={tagColor} text={tagText} />
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.buttonContainer}>
          {primaryButton && (
            <Button
              {...primaryButton}
              analytics={{
                linkLocation,
                linkModule,
                linkPosition: '1'
              }}
            />
          )}
          {secondaryButton && (
            <Button
              {...secondaryButton}
              analytics={{
                linkLocation,
                linkModule,
                linkPosition: '2'
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default CarouselCard
export { StatusTag }
